import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './MobileProductVariationsModal.css';

export function parseMoqQuantity(moq) {
  const m = String(moq || '').match(/(\d[\d,]*)/);
  if (!m) return 1;
  const n = parseInt(m[1].replace(/,/g, ''), 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function unitPriceUsd(product, qty) {
  const min = Number(product?.priceMin) || 0;
  const max = Number(product?.priceMax) || min;
  const mid = min + (max - min) * 0.45;
  if (qty >= 10000) return max;
  if (qty >= 1000) return mid;
  return min;
}

function shippingUsd(which, qty) {
  const base = which === 'premium' ? 62 : 48.5;
  return base + Math.min(qty * 0.02, 25);
}

function colorChoices(product) {
  const c = product?.color;
  if (!c || !String(c).trim()) return ['Default'];
  return String(c)
    .split(/[,;/|]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function MobileProductVariationsModal({
  open,
  onClose,
  product,
  thumbUrl,
  formatPrice,
  deliverCode,
  onConfirmAdd
}) {
  const minQty = useMemo(() => parseMoqQuantity(product?.moq), [product]);
  const colors = useMemo(() => colorChoices(product), [product]);
  const [color, setColor] = useState(colors[0]);
  const [qty, setQty] = useState(minQty);
  const [ship, setShip] = useState('standard');
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  useEffect(() => {
    if (open && product) {
      setQty(parseMoqQuantity(product.moq));
      setColor(colorChoices(product)[0]);
      setShip('standard');
      setBreakdownOpen(false);
    }
  }, [open, product]);

  const tiers = useMemo(() => {
    if (!product) return [];
    const min = Number(product.priceMin) || 0;
    const max = Number(product.priceMax) || min;
    const mid = min + (max - min) * 0.45;
    const moq = product.moq || '1 Unit';
    const u = (product.unit && String(product.unit).trim()) || 'units';
    return [
      { price: min, line: `Min. order: ${moq}` },
      { price: mid, line: `1,000–9,999 ${u}` },
      { price: max, line: `10,000+ ${u}` }
    ].map((t) => ({ ...t, label: formatPrice(t.price) }));
  }, [product, formatPrice]);

  const unit = useMemo(() => unitPriceUsd(product, qty), [product, qty]);
  const shipFee = useMemo(() => shippingUsd(ship, qty), [ship, qty]);
  const goodsUsd = useMemo(() => unit * qty, [unit, qty]);
  const subtotalUsd = useMemo(() => goodsUsd + shipFee, [goodsUsd, shipFee]);

  const dec = useCallback(() => {
    setQty((q) => Math.max(minQty, q - 1));
  }, [minQty]);

  const inc = useCallback(() => {
    setQty((q) => q + 1);
  }, []);

  const confirm = () => {
    if (!product) return;
    onConfirmAdd({
      quantity: qty,
      color,
      shipping: ship,
      unitPriceUsd: unit,
      shippingUsd: shipFee,
      lineTotalUsd: subtotalUsd
    });
    onClose();
  };

  if (!open || !product) return null;

  return (
    <div className="mpvar-dim" role="presentation" onClick={onClose}>
      <div
        className="mpvar-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mpvar-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mpvar-handle" />
        <div className="mpvar-head">
          <h2 id="mpvar-title" className="mpvar-title">
            Variations
          </h2>
          <button type="button" className="mpvar-x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="mpvar-row-img">
          <img className="mpvar-thumb" src={thumbUrl} alt="" />
          <div className="mpvar-prices">
            {tiers.map((t, i) => (
              <div key={i} className={i === 0 ? 'mpvar-price-line mpvar-price-line--hot' : 'mpvar-price-line'}>
                <span className="mpvar-p-amt">{t.label}</span>
                <span className="mpvar-p-desc">{t.line}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mpvar-color-row">
          <div>
            <div className="mpvar-lab">color ({colors.length})</div>
            <div className="mpvar-chips">
              {colors.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={c === color ? 'mpvar-chip mpvar-chip--on' : 'mpvar-chip'}
                  onClick={() => setColor(c)}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="mpvar-step">
            <button type="button" className="mpvar-step-btn" onClick={dec} aria-label="Decrease">
              −
            </button>
            <span className="mpvar-step-val">{qty}</span>
            <button type="button" className="mpvar-step-btn" onClick={inc} aria-label="Increase">
              +
            </button>
          </div>
        </div>

        <div className="mpvar-deliver">
          <span className="mpvar-pin">📍</span>
          Deliver to <strong>{deliverCode}</strong>
        </div>

        <div className="mpvar-ship-scroll">
          <button
            type="button"
            className={ship === 'standard' ? 'mpvar-ship mpvar-ship--on' : 'mpvar-ship'}
            onClick={() => setShip('standard')}
          >
            <div className="mpvar-ship-t">Standard</div>
            <div className="mpvar-ship-p">
              Shipping fee: {formatPrice(shippingUsd('standard', qty))} for {qty} {product.unit || 'units'}
            </div>
            <div className="mpvar-ship-g">Guaranteed delivery: 23 May – 07 Jun</div>
          </button>
          <button
            type="button"
            className={ship === 'premium' ? 'mpvar-ship mpvar-ship--on' : 'mpvar-ship'}
            onClick={() => setShip('premium')}
          >
            <div className="mpvar-ship-t">Premium</div>
            <div className="mpvar-ship-p">Shipping fee: {formatPrice(shippingUsd('premium', qty))}</div>
            <div className="mpvar-ship-g">Faster guaranteed window</div>
          </button>
        </div>

        <div className="mpvar-subfoot">
          <button type="button" className="mpvar-sub-toggle" onClick={() => setBreakdownOpen((v) => !v)}>
            <span>Subtotal</span>
            <span className="mpvar-sub-right">
              {formatPrice(subtotalUsd)}
              <span className={breakdownOpen ? 'mpvar-chev mpvar-chev--up' : 'mpvar-chev'}>▾</span>
            </span>
          </button>
          {breakdownOpen && (
            <div className="mpvar-break">
              <div>
                <span>Goods ({qty} × unit)</span>
                <span>{formatPrice(goodsUsd)}</span>
              </div>
              <div>
                <span>Shipping ({ship})</span>
                <span>{formatPrice(shipFee)}</span>
              </div>
            </div>
          )}
        </div>

        <button type="button" className="mpvar-add" onClick={confirm}>
          Add to cart
        </button>
      </div>
    </div>
  );
}
